import { isNonEmpty, isNotDefined } from '@sgme/fp';
import { sortBy, uniq } from 'lodash-es';
import { createSelector } from 'reselect';
import { getUserPreferencesSecondaryMarketProductSearchCodeType } from 'state/selectors';
import { getIsUserInternal } from 'state/selectors/common/user';
import type { AppState } from 'state/state.model';

import type { SecondaryMarketProductInternalUser } from '.';
import { getSecondaryMarketAstroProductStatus } from '../referential/astroProduct/astroProduct.selectors';
import type { LoadableUnderlying, SecondaryMarketProduct, SecondaryMarketProductGreeks } from './productSearch.model';
import {
  getRowIdFromSecondaryMarketProduct,
  hasTradeAuthorisation,
  limitOrderEnabledByRtTradeAuthorisation,
  limitOrderEnabledByStatus,
} from './productSearch.utils';

function selectProductSearch(state: AppState) {
  return state.secondaryMarket.productSearch;
}

export function getSecondaryMarketProductSearchCodeType(appState: AppState) {
  const state = selectProductSearch(appState);
  return state.codeType ?? getUserPreferencesSecondaryMarketProductSearchCodeType(appState);
}

const emptyItems: readonly SecondaryMarketProduct[] = [];
export function getSecondaryMarketProductSearchItems(appState: AppState): readonly SecondaryMarketProduct[] {
  const state = selectProductSearch(appState);
  const items = state.items;
  return Array.isArray(items) && items.length > 0 ? items : emptyItems;
}

export function getIsSearchingProducts(appState: AppState) {
  return selectProductSearch(appState)?.searchingKeys.length > 0;
}

export function getSecondaryMarketProductSearchUnderlyingsKeys(appState: AppState, rowId: string): string[] {
  const items = getSecondaryMarketProductSearchItems(appState);
  const item = items.find(i => i.rowId === rowId) as SecondaryMarketProductInternalUser | undefined;
  return uniq(
    [...(item?.closePriceGreeks ?? []), ...(item?.liveGreeks ?? [])].map(g => g.underlyingBcsKey).filter(key => !!key),
  ) as string[];
}

export function getSecondaryMarketProductSearchItemRowIds(appState: AppState) {
  return getSecondaryMarketProductSearchItems(appState)?.map(x => x.rowId);
}

export function getIsRefreshing(state: AppState) {
  return selectProductSearch(state).refreshingProductRowIds.length > 0;
}

export function getIsRefreshingByRowId(state: AppState, rowId: string) {
  return selectProductSearch(state).refreshingProductRowIds.includes(rowId);
}

export function getSecondaryMarketProductFromRowId(state: AppState, rowId: string) {
  return getSecondaryMarketProductSearchItems(state)?.find(row => getRowIdFromSecondaryMarketProduct(row) === rowId);
}

export function getSecondaryMarketProductIsinFromRowId(state: AppState, rowId: string) {
  const product = getSecondaryMarketProductFromRowId(state, rowId);
  return product?.isin;
}

export function getSecondaryMarketProductTabId(state: AppState, rowId: string) {
  const product = getSecondaryMarketProductFromRowId(state, rowId);
  return product?.isin ?? product?.fileCode ?? product?.bcsKeyPrefixedCode;
}

export function getSecondaryMarketProductClientFromRowId(state: AppState, rowId: string) {
  const product = getInternalProductByRowId(state, rowId);
  return product?.client;
}
export function getSecondaryMarketProductCustodianFromRowId(state: AppState, rowId: string) {
  const product = getInternalProductByRowId(state, rowId);
  return product?.custodian;
}

export function getProductsEditRequests(_: AppState) {
  return [];
}

export function getSecondaryMarketProductSearchSearchingCodes(appState: AppState) {
  const state = selectProductSearch(appState);
  return state.searchingKeys;
}

export function isSecondaryMarketProductSearchLoading(appState: AppState) {
  const searching = getSecondaryMarketProductSearchSearchingCodes(appState);
  return isNonEmpty(searching);
}

export function hasSecondaryMarketProductSearchTechnicalErrors(appState: AppState) {
  const state = selectProductSearch(appState);
  return state.hasTechnicalError;
}

export const getSecondaryMarketProductSearchColumns = createSelector(
  (state: AppState) => selectProductSearch(state).columns,
  getIsUserInternal,
  (columns, isInternal) => {
    if (isInternal) {
      return [{ field: 'rowId' as const, type: 'stringWithMasterDetail' }, ...columns];
    }
    return columns;
  },
);

export function getSecondaryMarketProductsPreferenceState(appState: AppState) {
  const state = selectProductSearch(appState);
  return state.blotterPreferenceState;
}

export const getSecondaryMarketRowIds = (state: AppState) =>
  getSecondaryMarketProductSearchItems(state).map(item => item.rowId);

export const getSecondaryMarketDlpRowIds = (state: AppState) =>
  getSecondaryMarketProductSearchItems(state)
    .filter(item => item.isin)
    .map(item => `ISIN::${item.isin}`);

export const getSecondaryMarketProductMissingCodes = (appState: AppState) => selectProductSearch(appState).notFoundKeys;

export function isRealtimeQuoteEnabled(state: AppState, rowId: string) {
  const row = getSecondaryMarketProductFromRowId(state, rowId);
  const productStatus = row?.isin !== undefined ? getSecondaryMarketAstroProductStatus(state, row?.isin) : 'Missing';
  return productStatus === 'Active';
}

export function isCpOrderEnabled(state: AppState, rowId: string) {
  const isInternal = getIsUserInternal(state);
  if (!isInternal) {
    return false;
  }
  const row = getSecondaryMarketProductFromRowId(state, rowId) as SecondaryMarketProductInternalUser | undefined;
  return hasTradeAuthorisation(row?.cpTradeAuthorisation);
}

export function isLiveQuoteEnabled(state: AppState, rowId: string) {
  const isInternal = getIsUserInternal(state);
  if (!isInternal) {
    return false;
  }
  const row = getSecondaryMarketProductFromRowId(state, rowId) as SecondaryMarketProductInternalUser | undefined;
  return hasTradeAuthorisation(row?.rtTradeAuthorisation);
}

export function isLimitOrderEnabled(state: AppState, rowId: string) {
  const row = getSecondaryMarketProductFromRowId(state, rowId);
  if (!row) {
    return false;
  }
  return limitOrderEnabledByStatus(row) && limitOrderEnabledByRtTradeAuthorisation(row);
}

export function getEditInfoForProductField(state: AppState, rowId?: string, field?: keyof SecondaryMarketProduct) {
  return isNotDefined(rowId) || isNotDefined(field)
    ? undefined
    : selectProductSearch(state).editState.items[rowId]?.[field];
}

const hydrateGreeksByUnderlyings = (
  greeks: SecondaryMarketProductGreeks[] = [],
  underlyings: Record<string, LoadableUnderlying>,
): SecondaryMarketProductGreeks[] => {
  const hydratedGreeks = greeks.map(g => {
    if (g.underlyingBcsKey && underlyings[g.underlyingBcsKey]) {
      return {
        label: underlyings[g.underlyingBcsKey].label,
        currency: underlyings[g.underlyingBcsKey].currency,
        bloombergTicker: underlyings[g.underlyingBcsKey].bloombergTicker,
        ...g,
      };
    }
    return g;
  });
  return sortBy(hydratedGreeks, g => {
    if (g.bloombergTicker) {
      return g.bloombergTicker?.toLocaleLowerCase();
    }
    return g.label?.toLocaleLowerCase();
  });
};

export const getHydratedCpGreeksForProduct = (state: AppState, rowId?: string) => {
  const item = getInternalProductByRowId(state, rowId);
  const productSearchState = selectProductSearch(state);
  if (!item) {
    return;
  }
  return hydrateGreeksByUnderlyings(item.closePriceGreeks, productSearchState.underlyingsDetails);
};

export const getHydratedLiveGreeksForProduct = (state: AppState, rowId?: string) => {
  const item = getInternalProductByRowId(state, rowId);
  const productSearchState = selectProductSearch(state);
  if (!item) {
    return;
  }
  return hydrateGreeksByUnderlyings(item.liveGreeks, productSearchState.underlyingsDetails);
};
export function getInternalProductByRowId(
  appState: AppState,
  rowId?: string,
): SecondaryMarketProductInternalUser | undefined {
  if (!getIsUserInternal(appState)) {
    return;
  }
  return getProductByRowId(appState, rowId);
}
export function getProductByRowId(appState: AppState, rowId?: string): SecondaryMarketProduct | undefined {
  if (rowId === undefined) {
    return;
  }

  const items = getSecondaryMarketProductSearchItems(appState);
  return items.find(i => i.rowId === rowId);
}

export function getFocusedProduct(appState: AppState): SecondaryMarketProduct | undefined {
  return getProductByRowId(appState, selectProductSearch(appState).focusedProductId);
}

export function isLoadingUnderlyingsForRowId(appState: AppState, rowId?: string): boolean {
  if (!rowId) {
    return false;
  }
  const item = getInternalProductByRowId(appState, rowId);
  const searchState = selectProductSearch(appState);
  const greeksKeys = uniq(
    [...(item?.closePriceGreeks ?? []), ...(item?.liveGreeks ?? [])].map(g => g.underlyingBcsKey).filter(key => !!key),
  ) as string[];
  return greeksKeys.some(k => searchState.underlyingsDetails[k]?.loadingStatus === 'loading');
}

export function canSaveBlotterPreferences(appState: AppState): boolean {
  return selectProductSearch(appState).canSaveBlotterPreferences;
}
